// /src/Components/Clients/AdminUpiPayment.jsx
import React, { useEffect, useMemo, useState, useContext, Fragment } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Building2, Filter, RefreshCcw, XCircle, CheckCircle } from "lucide-react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Dialog, Transition } from "@headlessui/react";

import useTransactions from "../Services/PaymentServices/useTransactions";
const PAGE_SIZE = 200;

export default function AdminUpiPayment({ Admin }) {
  const {getUpiReportPaynnow, refundProcessV1}=useTransactions();

  const AdminParse = typeof Admin === "string" ? JSON.parse(Admin) : Admin;
  const { api ,AllClients} = useContext(GlobalContext); 

  // Filters (defaults: yesterday→today)
  const today = () => new Date().toISOString().slice(0, 10);
  const yday = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(yday());
  const [endDate, setEndDate] = useState(today());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // success | failed | pending | all
  const [txnTypeFilter, setTxnTypeFilter] = useState("all");
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(""); // optional

  // Data
  const [clients, setClients] = useState([]); // optional dropdown (kept future-ready)
  const [upiTxns, setUpiTxns] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Refund dialog
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundBusy, setRefundBusy] = useState(false);

  /* -------------------- Load clients (optional use) -------------------- */
  useEffect(() => {
    let ignore = false;
    setClients(AllClients);
    
    return () => { ignore = true; };
  }, [AdminParse?.pkid, api]);

  /* -------------------- Fetch UPI transactions -------------------- */
const fetchUpiTxns = async () => {
  setLoading(true);

  try {
    const result =
      await getUpiReportPaynnow({
        userid: selectedClientId,
        fromDate: startDate,
        toDate: endDate,
      });

    if (result.success) {
      setUpiTxns(result.data);
      setPage(1);
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error(
      "Failed to fetch transactions"
    );
  } finally {
    setLoading(false);
  }
};


  // First load
  useEffect(() => {
    fetchUpiTxns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- Derived lists -------------------- */
  const sortedTxns = useMemo(
    () => [...upiTxns].sort((a, b) => new Date(b.indate || b.createdOn || 0) - new Date(a.indate || a.createdOn || 0)),
    [upiTxns]
  );

  const txnTypes = useMemo(() => {
    const set = new Set();
    for (const t of sortedTxns) if (t?.transactiontype) set.add(String(t.transactiontype));
    return ["all", ...Array.from(set)];
  }, [sortedTxns]);

  const filteredTxns = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusQ = statusFilter.toLowerCase();
    const n = (v) => Number(v ?? 0);
    const s = (v) => (v ?? "").toString().toLowerCase();

    return sortedTxns.filter((t) => {
      const matchesSearch = !q
        ? true
        : [t.merchantTransactionid, t.refid, t.utnno, t.creditorAccount, t.ifsCcode, t.instructionIdentification]
            .map(s)
            .some((v) => v.includes(q));

      const st = s(t.status);
      const matchesStatus = statusQ === "all" ? true : st === statusQ;

      const matchesTxnType =
        txnTypeFilter === "all" ? true : s(t.transactiontype) === txnTypeFilter.toLowerCase();

      const amt = n(t.transactionAmount ?? t.creditAmount ?? 0);
      const minOk = minAmt === "" ? true : amt >= Number(minAmt);
      const maxOk = maxAmt === "" ? true : amt <= Number(maxAmt);

      return matchesSearch && matchesStatus && matchesTxnType && minOk && maxOk;
    });
  }, [sortedTxns, search, statusFilter, txnTypeFilter, minAmt, maxAmt]);

  const totalPages = Math.max(1, Math.ceil(filteredTxns.length / PAGE_SIZE));
  const pageSafe = (p) => Math.min(totalPages, Math.max(1, p));
  const paginatedTxns = useMemo(
    () => filteredTxns.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredTxns, page]
  );

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTxnTypeFilter("all");
    setMinAmt("");
    setMaxAmt("");
    setPage(1);
  };

  /* -------------------- Refund flow (confirm + call) -------------------- */
  const openRefund = (row) => {
    setRefundTarget(row);
    setRefundOpen(true);
  };

const performRefund = async () => {
  if (!refundTarget) return;

  const amount = Math.trunc(
    Number(
      refundTarget?.creditAmount ??
      refundTarget?.transactionAmount ??
      0
    )
  ).toString();

  const transactionId = String(
    refundTarget?.refid ?? ""
  );

  setRefundBusy(true);

  try {
    const result =
      await refundProcessV1({
        userid: "",
        amount,
        transactionId,
      });

    if (result.success) {
      toast.success(result.message);
      // optionally:
      // await fetchUpiTxns();
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error(
      "Error processing refund"
    );
  } finally {
    setRefundBusy(false);
    setRefundOpen(false);
    setRefundTarget(null);
  }
};

  /* -------------------- UI -------------------- */
  const FilterBar = (
    <div className="rounded-2xl border bg-white p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
        <Filter size={16} /> Filters
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {/* (Optional) enable client filter when needed */}
        {/* <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Client</div>
          <select
            className="h-10 w-56 rounded-xl border bg-white px-3 text-sm outline-none"
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.us_code} value={c.us_code}>
                {c.us_code} — {c.us_name}
              </option>
            ))}
          </select>
        </label> */}

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Start</div>
          <input
            type="date"
            className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
            max={today()}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">End</div>
          <input
            type="date"
            className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
            max={today()}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Search</div>
          <div className="flex h-10 w-80 items-center gap-2 rounded-xl border px-3">
            <input
              className="h-full w-full outline-none text-sm"
              placeholder="Txn ID / Ref ID / Account / IFSC / UTN / Identification"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            {search && (
              <button className="rounded-full p-1 text-gray-500 hover:bg-gray-100" onClick={() => setSearch("")}>
                <XCircle size={16} />
              </button>
            )}
          </div>
        </label>

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Status</div>
          <select
            className="h-10 w-44 rounded-xl border bg-white px-3 text-sm outline-none"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </label>

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Txn Type</div>
          <select
            className="h-10 w-44 rounded-xl border bg-white px-3 text-sm outline-none"
            value={txnTypeFilter}
            onChange={(e) => { setTxnTypeFilter(e.target.value); setPage(1); }}
          >
            {txnTypes.map((t) => <option key={t} value={t}>{t === "all" ? "All" : t}</option>)}
          </select>
        </label>

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Min Amount</div>
          <input
            type="number"
            inputMode="decimal"
            className="h-10 w-36 rounded-xl border px-3 text-sm outline-none"
            placeholder="0"
            value={minAmt}
            onChange={(e) => { setMinAmt(e.target.value); setPage(1); }}
          />
        </label>

        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Max Amount</div>
          <input
            type="number"
            inputMode="decimal"
            className="h-10 w-36 rounded-xl border px-3 text-sm outline-none"
            placeholder="Any"
            value={maxAmt}
            onChange={(e) => { setMaxAmt(e.target.value); setPage(1); }}
          />
        </label>

        <div className="ml-auto flex gap-2">
          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={clearFilters}
            disabled={loading}
          >
            Clear
          </button>
          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
            onClick={fetchUpiTxns}
            disabled={loading}
          >
            {loading ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>

      {/* Active badges */}
      {(search || statusFilter !== "all" || txnTypeFilter !== "all" || minAmt || maxAmt) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {search && <Badge onClear={() => setSearch("")}>Search: {search}</Badge>}
          {statusFilter !== "all" && <Badge onClear={() => setStatusFilter("all")}>Status: {statusFilter}</Badge>}
          {txnTypeFilter !== "all" && <Badge onClear={() => setTxnTypeFilter("all")}>Type: {txnTypeFilter}</Badge>}
          {(minAmt || maxAmt) && (
            <Badge onClear={() => { setMinAmt(""); setMaxAmt(""); }}>
              Amount: {minAmt || "0"} – {maxAmt || "∞"}
            </Badge>
          )}
        </div>
      )}
    </div>
  );

  // Money helper
  const money = (v) => (v == null || isNaN(+v) ? "₹ 0.00" : `₹ ${Number(v).toFixed(2)}`);

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">UPI Payments</h1>
            <p className="text-sm text-gray-500">Monitor and manage UPI-based transactions</p>
          </div>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          onClick={fetchUpiTxns}
          disabled={loading}
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {FilterBar}

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#01C37D] text-white">
              <tr>
                {[
                  "S/N",
                  "Merchant Txn ID",
                  "Created On",
                  "Ref ID",
                  "UTN No",
                  "Credit Amt",
                  "Status",
                  "Txn Type",
                  "Creditor Acc",
                  "IFSC",
                  "Debit Amt",
                  "Balance",
                  "CGST",
                  "SGST",
                  "Charges",
                  "Txn Amt",
                  // "Action" // Uncomment if you want Refund button in table
                ].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedTxns.length === 0 && !loading && (
                <tr>
                  <td colSpan={16} className="px-4 py-8 text-center text-gray-500">
                    No records found for current filters.
                  </td>
                </tr>
              )}

              {paginatedTxns.map((item, idx) => (
                <tr key={`${item.merchantTransactionid}-${idx}`} className="border-t">
                  <td className="px-4 py-2">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                  <td className="px-4 py-2 font-semibold">{item.merchantTransactionid || "—"}</td>
                  <td className="px-4 py-2">{item.createdOn || item.indate || "—"}</td>
                  <td className="px-4 py-2 font-semibold">{item.refid || "—"}</td>
                  <td className="px-4 py-2">{item.utnno || "—"}</td>
                  <td className="px-4 py-2">{money(item.creditAmount)}</td>
                  <td className="px-4 py-2">
                    {String(item.status || "").toLowerCase() === "success" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        Success
                      </span>
                    ) : (
                      (item.status || "—")
                    )}
                  </td>
                  <td className="px-4 py-2">{item.transactiontype || "—"}</td>
                  <td className="px-4 py-2">{item.creditorAccount || "—"}</td>
                  <td className="px-4 py-2">{(item.ifsCcode || "").toUpperCase() || "—"}</td>
                  <td className="px-4 py-2">{money(item.debitAmount)}</td>
                  <td className="px-4 py-2">{money(item.balance)}</td>
                  <td className="px-4 py-2">{money(item.cgstAmount)}</td>
                  <td className="px-4 py-2">{money(item.sgstAmount)}</td>
                  <td className="px-4 py-2">{money(item.chargesAmount)}</td>
                  <td className="px-4 py-2">{money(item.transactionAmount)}</td>
                  {/* <td className="px-4 py-2">
                    <button
                      className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                      onClick={() => openRefund(item)}
                    >
                      <CheckCircle size={14} /> Refund
                    </button>
                  </td> */}
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={16} className="px-4 py-6 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredTxns.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => pageSafe(p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b>
            </span>
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => pageSafe(p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Refund Confirm Dialog (kept but hidden by default) */}
      <Confirm
        open={refundOpen}
        title="Confirm Refund"
        message={
          refundTarget ? (
            <div className="text-sm">
              <div><b>Transaction ID:</b> {String(refundTarget?.refid || "—")}</div>
              <div><b>Amount:</b> {money(refundTarget?.creditAmount ?? refundTarget?.transactionAmount)}</div>
            </div>
          ) : (
            "Are you sure you want to initiate refund?"
          )
        }
        confirmText={refundBusy ? "Processing…" : "Refund"}
        onClose={() => !refundBusy && setRefundOpen(false)}
        onConfirm={() => !refundBusy && performRefund()}
        danger
      />
    </div>
  );
}

/* ---------- tiny badge ---------- */
function Badge({ children, onClear }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs">
      {children}
      <button className="rounded-full px-1.5 text-gray-500 hover:bg-gray-200" onClick={onClear}>
        ✕
      </button>
    </span>
  );
}

/* ---------- minimal confirm dialog (Headless UI) ---------- */
function Confirm({ open, onClose, onConfirm, title, message, confirmText = "Confirm", danger }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
              <div className="mt-2 text-gray-700">{typeof message === "string" ? <p className="text-sm">{message}</p> : message}</div>
              <div className="mt-4 flex justify-end gap-2">
                <button className="h-9 rounded-xl border px-3 text-sm" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className={`h-9 rounded-xl px-3 text-sm font-medium text-white ${danger ? "bg-rose-600 hover:bg-rose-700" : "bg-[#01C37D] hover:bg-[#01C37D]/90"}`}
                  onClick={onConfirm}
                >
                  {confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
